<template>
  <section id="lm-datatable-builder">
    <h3>Data Table Builder</h3>
    <b-row>
      <LmTreeSelect
        v-if="!isOnEditMode"
        class="col-12"
        label="¿Qué datos desea listar?"
        pathToGetOptions="/datatables/constructor-options"
        v-model="builder.origin"
        @input="onBuilderOriginChange"
      />
      <template v-if="builder.origin">
        <LmTextInput
          class="col-12 col-md-4"
          label="Nombre del listado"
          v-model="$v.name.$model"
          :v="$v.name"
          name="name"
          @blur="validateSlug"
        />
        <LmTextInput
          class="col-12 col-md-4"
          label="Alias del listado"
          v-model="$v.alias.$model"
          :v="$v.alias"
          name="alias"
        />
        <LmNumberInputV2
          v-if="mode === 'list'"
          class="col-12 col-md-4"
          label="Resultados por página"
          v-model="size"
        />
        <LmNumberInputV2
          v-if="mode === 'box'"
          class="col-12 col-md-4"
          label="Filas por página"
          v-model="rows"
          @input="onRowsPerPageChange"
        />
        <LmTextInput
          class="col-12 col-md-4"
          label="Slug"
          v-model="$v.locationSlug.$model"
          :v="$v.locationSlug"
          name="locationSlug"
        />
        <div class="col-12 d-flex mb-4" style="gap: 1rem">
          <b-form-checkbox
            v-model="mode"
            value="box"
            unchecked-value="list"
            @input="onModeChange"
          >
            Modo caja
          </b-form-checkbox>
          <b-form-checkbox
            v-model="selectable"
            :disabled="mode !== 'list'"
          >
            Seleccionble
          </b-form-checkbox>
        </div>
      </template>
    </b-row>

    <template v-if="!isLoading">
      <section class="bg-light p-4 mb-4 rounded" v-if="builder.fields.length">
        <!-- list mode -->
        <template v-if="mode === 'list'">
          <div class="d-flex justify-content-between">
            <h4>Columnas</h4>
            <b-dropdown text="Columnas" menu-class="px-4" variant="info" right>
              <b-form-checkbox
                class="py-2"
                switch
                v-for="(item, index) in builder.fields"
                :key="index"
                v-model="item.visible"
                :disabled="item.primaryColumn"
              >
                <span class="text-nowrap">{{ item.label }}</span>
              </b-form-checkbox>
            </b-dropdown>
          </div>
          <draggable
            tag="div"
            class="row scroll align-items-stretch"
            v-model="builder.fields"
            animation="150"
            handle=".handle"
          >
            <b-col
              v-for="(item, index) in visibleFields" :key="index"
              cols="3"
              class="py-4 handle"
            >
              <div class="rounded shadow p-4 h-100" :class="{'border border-primary': item.primaryColumn}">
                <div class="d-flex align-items-center justify-content-between">
                  <h6 :id="item.key">{{ item.label }}</h6>
                  <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none column-label-edit-button text-secondary" no-caret right>
                    <template #button-content>
                      <i class="mdi mdi-tune"></i>
                      <span class="sr-only">Edit</span>
                    </template>
                    <div class="p-2 column-label-edit-dropdown">
                      <LmTextInput v-model="item.label" class="mb-0" label="Título de la columna" />
                      <b-form-checkbox
                        class="py-2"
                        switch
                        v-model="item.sortable"
                      >
                        <span class="text-nowrap">Columna ordenable</span>
                      </b-form-checkbox>
                    </div>
                  </b-dropdown>
                </div>
                <hr class="w-100" />

                <p v-if="item.stickyColumn" class="text-primary">
                  <i class="mdi mdi-star-outline info-icon"></i>
                  Columna primaria
                </p>
                <p v-if="item.description">
                  <i class="mdi mdi-information-outline info-icon"></i>
                  {{ item.description }}
                </p>
                <p v-if="item.sortable">
                  <i class="mdi mdi-sort-alphabetical-ascending info-icon"></i>
                  Esta columna se puede ordenar
                </p>

                <div v-if="item.render === 'actions'">
                  <p>Opciones</p>
                  <b-form-checkbox
                    class="py-2"
                    switch
                    v-for="(item, index) in item.options"
                    :key="index"
                    v-model="item.visible"
                  >
                    <span class="text-nowrap">{{ item.label }}</span>
                  </b-form-checkbox>
                </div>
              </div>
            </b-col>
          </draggable>
        </template>
        <!-- box mode -->
        <template v-else-if="mode === 'box'">
          <h4>Cajas</h4>
          <div class="d-flex justify-content-center box-preview">
            <b-card class="w-75" :style="`border-top: 4px solid ${accentColor}`">
              <p v-if="primaryField.description">
                <i class="mdi mdi-information-outline info-icon"></i>
                {{ primaryField.description }}
              </p>
              <LmSelect
                label="Tamaño de la caja"
                :options="boxModeColsOptions"
                v-model="primaryField.boxCols"
                @input="onBoxColsChange"
              />
            </b-card>
          </div>
        </template>
      </section>

      <section class="bg-light p-4 mb-4 rounded" v-if="builder.fields.length">
        <div class="d-flex justify-content-between">
          <h4>Filtros por defecto</h4>
          <b-dropdown text="Filtros del cliente" menu-class="px-4" variant="info" right>
            <b-form-checkbox
              class="py-2"
              switch
              v-for="(item, index) in clientFilters"
              :key="index"
              v-model="item.visible"
            >
              <span class="text-nowrap">{{ item.label }}</span>
            </b-form-checkbox>
          </b-dropdown>
        </div>
        <div class="d-flex align-items-center flex-wrap mt-4" style="gap: 1rem">
          <div
            v-for="(item, index) in constructorFilters"
            :key="index"
          >
            <Component
              :key="item.elastic_key"
              :is="defineComponent(item.type)"
              v-bind="item"
              @change="onFilterChange(item, $event)"
            />
          </div>
        </div>
      </section>

      <b-row>
        <LmButton
          class="col-auto"
          buttonClass="btn-secondary"
          :value="$t('cancel')"
          @click="onCancel"
        />
        <LmButton
          v-if="!deleted"
          class="col-auto"
          buttonClass="btn-primary"
          value="Guardar"
          @click="onSave"
        />
        <LmButton
          v-if="!deleted"
          class="col-auto"
          buttonClass="btn-danger"
          action="delete"
          value="Eliminar"
          confirmationTitle="¿Quiéres eliminar esta table de datos?"
          @click="onDelete"
        />
        <LmButton
          v-if="deleted"
          class="col-auto"
          buttonClass="btn-info"
          value="Re activar"
          @click="onReactivate"
        />
      </b-row>
    </template>

    <!-- loading state -->
    <template v-else>
      <b-row class="mb-4">
        <b-col v-for="item in 5" :key="item">
          <b-skeleton-img height="250px"></b-skeleton-img>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col cols="auto" v-for="item in 2" :key="item">
          <b-skeleton type="button"></b-skeleton>
        </b-col>
      </b-row>
    </template>
  </section>
</template>

<script>
// Components
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmSelect from '@/components/form/elements/LmSelect'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmNumberInputV2 from '@/components/form/elements/LmNumberInputV2'
import LmButton from '@/components/form/elements/LmButton'
import FilterSelect from './components/FilterSelect'
import FilterDate from './components/FilterDate'

// Vuex
import { mapGetters } from 'vuex'

// Utils
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts'
import draggable from 'vuedraggable'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  props: {
    isOnEditMode: {
      type: Boolean,
      required: true
    },
    slug: {
      type: String,
      default: null
    }
  },
  components: {
    LmSelect,
    LmTreeSelect,
    LmTextInput,
    LmNumberInputV2,
    LmButton,
    FilterSelect,
    FilterDate,
    draggable
  },
  data () {
    return {
      isLoading: false,
      id: '',
      name: '',
      alias: '',
      locationSlug: '',
      size: 10,
      rows: 4,
      mode: 'list',
      selectable: false,
      deleted: false,
      builder: {
        origin: null,
        fields: [],
        filters: [],
        buttons: []
      }
    }
  },
  validations: {
    name: { required },
    alias: { required },
    locationSlug: { required }
  },
  computed: {
    ...mapGetters('settings', ['accentColor']),
    visibleFields () {
      return this.builder.fields.filter(field => field.visible)
    },
    primaryField () {
      return this.builder.fields.find(field => field.primaryColumn)
    },
    boxModeColsOptions () {
      return {
        content: [
          { text: '1/4', value: 3 },
          { text: '1/3', value: 4 },
          { text: '1/2', value: 6 }
        ]
      }
    },
    constructorFilters () {
      return this.builder.filters.filter(el => !el.hideOnConstructor)
    },
    clientFilters () {
      return this.builder.filters.filter(el => !el.hideOnClient)
    }
  },
  methods: {
    validateForm (validations) {
      return new Promise((resolve, reject) => {
        let error = false
        validations.$touch()
        error = validations.$error
        if (error) {
          for (const key in this) {
            if (validations.hasOwnProperty(key)) {
              if (validations[key].$error) {
                const selector = this.$el.querySelector(`[name="${key}"]`)
                selector.scrollIntoView({ behavior: 'smooth' })
                break
              }
            }
          }
          this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error de validación')
          reject(new Error('validation error'))
        } else {
          resolve(true)
        }
      })
    },
    defineComponent (type) {
      const components = {
        selector: 'FilterSelect',
        'time-ago': 'FilterSelect',
        'date-range': 'FilterDate'
      }
      return components[type]
    },
    onFilterChange (item, event) {
      item.pick = event
    },
    onCancel () {
      this.$router.go(-1)
    },
    async onSave () {
      try {
        await this.validateForm(this.$v)
        const builderData = {
          name: this.name,
          alias: this.alias,
          slug: this.locationSlug,
          size: this.size,
          mode: this.mode,
          selectable: this.selectable,
          builder: this.builder
        }
        let res = null
        if (this.isOnEditMode) {
          res = await commonsApi.axiosPutWrapper(`/datatables/${this.id}`, builderData)
        } else {
          res = await commonsApi.axiosPostWrapper('/datatables', builderData)
        }
        this.lmToastSuccessInternalLink(
          `/listado/${res.data.slug}`,
          `Se guardó la tabla ${this.name} corectamente, échale un vistazo`,
          'Acción realizada con éxito'
        )
        if (!this.isOnEditMode) {
          this.$router.replace(`/datatable/editar/${res.data.slug}`)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async onDelete () {
      try {
        await commonsApi.axiosDeleteWrapper(`/datatables/delete/${this.id}`)
        this.lmToastSuccessMessage(
          `Se eliminó la tabla de datos correctamente.`,
          'Acción realizada con éxito'
        )
        this.deleted = true
      } catch (error) {
        console.log(error)
      }
    },
    async onReactivate () {
      try {
        await commonsApi.axiosPutWrapper(`/datatables/reactivate/${this.id}`)
        this.lmToastSuccessMessage(
          `Se activó la tabla de datos correctamente.`,
          'Acción realizada con éxito'
        )
        this.deleted = false
      } catch (error) {
        console.log(error)
      }
    },
    async onBuilderOriginChange (event) {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosGetWrapper(`/datatables/${event.id}/constructor`)
        this.builder.fields = res.data.fields
        this.builder.filters = res.data.filters
        this.builder.buttons = res.data.buttons
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onRowsPerPageChange (event) {
      const rows = event
      const box = this.primaryField.boxCols
      const width = 12
      this.size = (width / box) * rows
    },
    onBoxColsChange () {
      this.onRowsPerPageChange(this.rows)
    },
    onModeChange () {
      this.selectable = false
      this.onRowsPerPageChange(this.rows)
    },
    async validateSlug () {
      const res = await commonsApi.axiosPostWrapper('/datatables/validate-slug', {
        id: this.id,
        slug: this.locationSlug
      })
      this.locationSlug = res.data
    }
  },
  async created () {
    try {
      this.isLoading = true
      if (this.isOnEditMode) {
        const res = await commonsApi.axiosGetWrapper(`/datatables/${this.slug}/builder`)
        this.id = res.data._id
        this.name = res.data.name
        this.locationSlug = res.data.slug
        this.alias = res.data.alias
        this.size = res.data.size
        this.selectable = res.data.selectable
        this.mode = res.data.mode
        this.builder.fields = res.data.builder.fields
        this.builder.filters = res.data.builder.filters
        this.builder.buttons = res.data.builder.buttons
        this.builder.origin = res.data.builder.origin
        this.deleted = res.data.deleted
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
    this.$watch('name', val => {
      this.locationSlug = val.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '')
    })
  }
}
</script>

<style lang="sass">
#lm-datatable-builder
  .box-preview
    padding: 2rem 0
  .info-icon
    font-size: 1rem
  .column-label-edit-dropdown
    width: 200px
  .btn.column-label-edit-button
    font-size: 1.2rem
    &:focus
      box-shadow: none
      outline: none
  .scroll
    flex-wrap: nowrap
    overflow-x: auto
    padding: 2rem 0
    &:hover
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: #888
    &::-webkit-scrollbar
      height: 8px
      width: 8px
    &::-webkit-scrollbar-thumb
      border-radius: 20px
      background: #888
    &::-webkit-scrollbar-thumb:hover
      background: #555
  </style>
